import React, { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import type { Message } from '@core/models'
import { Identicon } from '@/components/Identicon'
import { cn } from '@/lib/utils'

interface MessageListProps {
  messages: Message[]
  guestName: string
  operatorName: string
  loading: boolean
}

const formatTime = (dateString?: string) => {
  if (!dateString) return ''
  return new Intl.DateTimeFormat('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateString))
}

const formatDateSeparator = (dateString?: string) => {
  if (!dateString) return ''
  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(dateString))
}

const MessageList = observer(({ messages, guestName, operatorName, loading }: MessageListProps) => {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  const renderMessage = (message: Message, index: number) => {
    const isOperator = !!message.user
    const senderName = isOperator ? operatorName : guestName
    const showDateSeparator =
      index === 0 ||
      (messages[index - 1] &&
        new Date(messages[index - 1].created).toDateString() !== new Date(message.created).toDateString())

    return (
      <React.Fragment key={message.id || index}>
        {showDateSeparator && (
          <div className="flex items-center gap-3 my-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">{formatDateSeparator(message.created)}</span>
            <div className="h-px flex-1 bg-border" />
          </div>
        )}

        <div className={cn('flex gap-2 mb-3', isOperator && 'flex-row-reverse')}>
          <div className="shrink-0 mt-1">
            <Identicon source={`${message.guest ? 'g:' : 'u:'}${isOperator ? message.user : message.guest}`} size={32} />
          </div>

          <div className={cn('max-w-[70%] space-y-0.5', isOperator && 'items-end text-right')}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground" style={{ justifyContent: isOperator ? 'flex-end' : 'flex-start' }}>
              <span className="font-medium">{senderName}</span>
              <span>{formatTime(message.created)}</span>
            </div>

            <div
              className={cn(
                'inline-block rounded-lg px-3 py-2 break-words text-sm',
                isOperator
                  ? 'bg-primary text-primary-foreground rounded-tr-none'
                  : 'bg-muted rounded-tl-none'
              )}
            >
              {message.type === 'image' && message.image_url ? (
                <a href={message.image_url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={message.image_url}
                    alt="添付画像"
                    className="max-h-64 max-w-full rounded-md cursor-pointer"
                  />
                </a>
              ) : (
                <span className="whitespace-pre-wrap">{message.body}</span>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    )
  }

  return (
    <div className="flex-1 overflow-auto px-6 py-4">
      {loading && <div className="text-sm text-muted-foreground py-8 text-center">読み込み中...</div>}
      {!loading && messages.length === 0 && (
        <div className="text-sm text-muted-foreground py-8 text-center">
          まだメッセージがありません
        </div>
      )}
      {messages.map((message, index) => renderMessage(message, index))}
      <div ref={bottomRef} />
    </div>
  )
})

export { MessageList }
export default MessageList